<template>
  <div class="flex flex-col md:w-2/3 w-full whitespace-nowrap relative">
    <!-- Title -->
    <div v-if="showTitle" class="font-mono md:text-2xl text-xl font-bold text-center pb-10">
      Nevua Markets
    </div>

    <!-- Subtitle & animation -->
    <div v-if="showTitle" class="flex flex-col font-mono text-normal font-thin mb-8 sm:items-center ml-4 sm:ml-0 justify-left">
      <div>
        <span class="mr-2">Create</span> 
        <img src="/assets/polymarket.svg" alt="Polymarket" class="inline-block h-5 w-5" />
        <span class="font-bold">Polymarket</span> 
        <span class="ml-2">watchlists.</span>
      </div>
      <div class="flex flex-row items-center justify-left">
        <span>Get <span class="font-bold">real-time alerts</span></span>
        <div class="logo-flipper h-full w-24 ml-2">
          <div 
            ref="logoContainer"
            class="logo-container" 
            :style="{ transform: `rotateX(${flipDegrees}deg)` }">
            <div class="logo-face logo-front">
              <span>{{ logos[currentLogoIndex].name }}</span>
              <img 
                :src="logos[currentLogoIndex].logo" 
                :alt="logos[currentLogoIndex].name" 
                class="h-5 w-5 inline-block "
              />
            </div>
            <div class="logo-face logo-back">
              <span>{{ logos[nextLogoIndex].name }}</span>
              <img 
                :src="logos[nextLogoIndex].logo" 
                :alt="logos[nextLogoIndex].name" 
                class="h-5 w-5 inline-block"
              />
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Search Bar and Search Button -->
    <div class="flex flex-row relative">
      <div class="flex flex-row w-full rounded-lg border bg-gray-50">
        <SearchOptions 
          @toggle-match-terms="handleMatchTermsToggle" 
          :disabled="false && searchOptionsDisabled()"
        />
        <div class="flex-grow">
          <input 
            ref="searchInput" 
            :placeholder="getPlaceholderText()"
            class="w-full h-full rounded-lg px-4 flex items-center md:text-xl text-base font-mono bg-gray-50 focus:outline-none leading-relaxed break-all resize-none"
            @blur="handleBlur()" 
            @input="handleInput" 
            @keyup.enter="handleSaveSearch()"
            @keydown.esc="clearInput()"
            @click="handleClick()"
            />
        </div>

        <button 
          :disabled="isSearchActionDisabled()"
          class="px-4 py-2 rounded flex items-center justify-center relative w-42 h-10" 
          @click="handleSaveSearch">

          <!-- Create watchlist icon -->
          <div v-if="store.currentWatchListId === null && !isLoading && false" class="h-full w-full flex items-center">
            <!-- <img src="/assets/search.svg" alt="Search" class="h-5 w-5" /> -->
             <div 
             :class="[isSearchActionDisabled() ? 'opacity-50' : 'opacity-100', 'border border-gray-300 rounded-md px-2 py-1 font-mono text-sm']"> 
              Create Watchlist 
              <i class="fa-regular fa-bookmark" /> 
            </div>
          </div>

          <div 
            v-if="store.currentWatchListId === null && !isLoading && true" 
            :class="[!hasText ? 'opacity-30' : 'opacity-100', 'h-full w-full flex items-center']">
            <img src="/assets/search.svg" alt="Search" class="h-5 w-5" />
 
              <!-- <i class="fa-regular fa-bookmark" />  -->
          </div>

          <!-- Loading icon -->
          <div v-if="isLoading" class="flex items-center justify-center h-full w-full">
            <i class="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500" />
          </div>

          <!-- Edit icon -->
          <div v-if="store.currentWatchListId !== null && !isLoading && !isEditingQuery" class="h-full flex items-center">
            <i 
              class="fas fa-pencil-alt h-5 w-5 text-gray-400 hover:text-gray-500" 
              v-tippy="{ content: 'Edit watchlist query', placement: 'bottom' }"
              />
          </div>

          <!-- Save icon -->
          <div v-if="store.currentWatchListId !== null && !isLoading && isEditingQuery" class="h-full flex items-center">
            <i 
              class="fas fa-check h-5 w-5 text-gray-400 hover:text-gray-500" 
              v-tippy="{ content: 'Save watchlist query', placement: 'bottom' }"
              />
          </div>

        </button>
      </div>

      <!-- Floating Search Results Container -->
      <div v-if="searchResults.length > 0 && showSearchResults" class="absolute top-full left-0 z-50 w-full shadow-xl mt-1">
        <div class="bg-white shadow-lg rounded-lg border w-full">
          <div class="p-2 text-sm text-gray-600 border-b flex flex-col">
            {{ searchResults.length }} results
            <span v-if="store.currentWatchListId && comparisonSummary" class="flex flex-col">
              <span v-if="comparisonSummary.newCount > 0" class="ml-1 text-green-600">
                {{ comparisonSummary.newCount }} new markets
              </span>
              <div class="flex flex-row items-center justify-between w-full">
                <span v-if="comparisonSummary.removedCount > 0" class="ml-1 text-red-600">
                  {{ comparisonSummary.removedCount }} watchlist markets no longer matching query
                </span>
              
              <!-- Toggle for removing non-matching items -->
                <div v-if="comparisonSummary.removedCount > 0" class="mt-1 flex items-center">
                  <button 
                    @click.stop="removeNonMatchingItems = !removeNonMatchingItems; handleRemoveNonMatchingItems()"
                    @mousedown.prevent
                    type="button"
                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    :class="removeNonMatchingItems ? 'bg-blue-500' : 'bg-gray-200'"
                    aria-pressed="false"
                  >
                    <span class="sr-only">Remove non-matching items</span>
                    <span
                      class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      :class="removeNonMatchingItems ? 'translate-x-4' : 'translate-x-0'"
                    ></span>
                  </button>
                  <span 
                  class="ml-2 text-xs text-red-600"
                  v-tippy="{ content: 'Remove non-matching items and any alerts specific to them', placement: 'bottom' }"
                  >Remove <i class="fa-solid fa-info-circle" /></span>
                </div>
              </div>
            </span>
            
          </div>
          <div v-if="store.currentWatchListId === null" class="max-h-72 overflow-y-auto">
            <div v-for="(result, index) in searchResults" :key="index"
              class="p-4 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
              @click.stop.prevent="selectResult(result)"
              @mousedown.prevent>
              <!-- <div class="font-semibold">{{ result.type === 'market' ? 'M:' : 'E:' }} {{ result.title }}</div> -->
              <div class="font-semibold">{{ result.title }}</div>
            </div>
          </div>
          <div v-else class="max-h-72 overflow-y-auto">
            <div v-for="(result, index) in searchResults" :key="index"
              class="p-4 border-b last:border-b-0 hover:bg-gray-50">
              <!-- <div class="font-semibold">{{ result.type === 'market' ? 'M:' : 'E:' }} {{ result.title }}</div> -->
              <div class="font-semibold">{{ result.title }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Options context -->
    <div class="text-xs text-gray-400 text-center mt-1 mb-1">
      <span v-if="matchAnyTerm">Searching events with titles or tags matching <span class="font-bold">any</span> keyword</span>
      <span v-else>Searching events with titles or tags matching <span class="font-bold">all</span> keywords</span>
    </div>

    <!-- Search Examples -->
     <div v-if="showTitle" class="mt-4">
      <div 
        class="text-center text-normal text-gray-500 font-mono">
        Search examples:
      </div>
      <div class="flex flex-wrap gap-2 justify-center px-4 max-w-3xl mx-auto">
        <button
          v-for="example in searchExamples"
          @click="$refs.searchInput.value = example; $refs.searchInput.focus(); handleInput({ target: $refs.searchInput })"
          class="px-3 py-1 text-sm bg-gray-50 text-gray-600 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors duration-200 font-mono whitespace-nowrap"
        >
          {{ example }}
        </button>
      </div>
    </div>


  </div>
</template>

<script>
/*
  Search flow:

  Unauthenticated user:
  - Search for events
  - Hit 'Search' button or enter
  - Temporary watchlist is created and saved to local storage. Only one per time
  - Temporary watchlist table is shown
  - Login modal pops up when clicking on 'Crete' on alert.
  - After login, watchlist is saved to db.

  Authenticated user:
  - Search for events
  - Hit 'Search' button or enter
  - Watchlist is saved to db
  
  Edit flow:
  - Show how many new events will be added and how many events will be removed
  - If removed > 0, show modal with confirmation
  
*/
import { 
  store, 
  saveSearchResultsToNewWatchList,
  updateWatchListQueryAndEvents,
} from '../store';
import { useDebounceFn } from '@vueuse/core';
import { TransitionRoot } from '@headlessui/vue';

import apiClient from '../utils/axios.js';
import SearchOptions from './menus/SearchOptions.vue';
import * as Sentry from '@sentry/vue';

export default {
  name: 'SearchBar',
  components: {
    SearchOptions,
    TransitionRoot
  },
  props: {
    showTitle: {
      type: Boolean,
      default: false
    },
    query: {
      type: String,
      default: ''
    }
  },
  emits: ['refetch-events-for-watchlist'],
  data() {
    return {
      store,
      isLoading: false,
      hasText: false,
      searchResults: [],
      showSearchResults: false,
      matchAnyTerm: true,
      debouncedHandleInput: null,
      isEditingQuery: false,
      currentLogoIndex: 0,
      nextLogoIndex: 0,
      flipDegrees: 0,
      comparisonSummary: null,
      removeNonMatchingItems: false,
      removeNonMatchingItemsToggled: false,
      logos: [
        {
          name: 'on Telegram',
          logo: '/assets/endpoint-logos/telegram-logo.svg',
          class: 'h-5 w-5 inline-block',
        },
        {
          name: 'on Discord',
          logo: '/assets/endpoint-logos/discord-logo.svg',
          class: 'h-5 w-5 inline-block'
        },
        {
          name: 'via Webhook',
          logo: '/assets/endpoint-logos/webhook-logo.svg',
          class: 'h-5 w-5 inline-block '
        }
      ],
      searchExamples: [
        '"Breaking News"',
        'Bitcoin',
        'Geopolitics "Middle East"',
        'Tariffs',
        'Russia Ukraine',
        '"Middle East" Iran Israel -Eurovision',
      ]
    };
  },
  created() {
    this.debouncedHandleInput = useDebounceFn(async function(event) {
      this.isLoading = true;
      this.updateHasText(event);

      const query = event.target.value.trim();

      // Reset search results if query is empty or too short
      if (query.length < 3) {
        this.searchResults = [];
        this.isLoading = false;
        return;
      }

      try {
        const response = await apiClient.get(`https://api.nevua.markets/search?query=${encodeURIComponent(query)}&matchAny=${this.matchAnyTerm}`);

        // const wait = ms => new Promise(resolve => setTimeout(() => resolve({status: 200, data: []}), ms));
        // const response = await wait(2000);


        if (response.status !== 200) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        //console.log('Search results:', JSON.stringify(response.data, null, 2));

        this.searchResults = [...response.data]
        this.showSearchResults = true
        
        // Compare search results with watchlist if we're on a watchlist page
        this.compareSearchResultsWithWatchlist();

        //  this.searchResults = response.data.flatMap(event => {
        //     if (event.markets.length > 0) {
        //       return event.markets.map(market => ({
        //         type: 'market',
        //         title: market.question
        //       }));
        //     } else {
        //       return [{
        //         type: 'event',
        //         title: event.title
        //       }];
        //     }
        //   });

        //console.log('Search results:', JSON.stringify(this.searchResults, null, 2));
      } catch (error) {
        Sentry.setExtra('method', 'debouncedHandleInput');
        Sentry.captureException(error);
        console.error('Search error:', error);
        this.searchResults = [];
      } finally {
        this.isLoading = false;
      }
    }, 300, { maxWait: 1000 });
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyPress);
    this.startLogoFlipping();
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyPress);
    if (this.flipInterval) {
      clearInterval(this.flipInterval);
    }
  },
  methods: {
    handleBlur() {
      // Add a small delay to allow click events to process first
      setTimeout(() => {
        if (this.removeNonMatchingItemsToggled) {
          this.removeNonMatchingItemsToggled = false;
          // refocus on search input
          this.$refs.searchInput.focus();
        } else {
          this.clearInput();
        }
      }, 200);
    },
    handleRemoveNonMatchingItems() {
      console.log('Remove non-matching items:', this.removeNonMatchingItems);
      this.removeNonMatchingItemsToggled = true;
      // Immediately refocus on the search input to prevent blur
      this.$nextTick(() => {
        this.$refs.searchInput.focus();
      });
    },
    isSearchActionDisabled() {
      // If in main search page, disable button if loading or no text
      if (!store.currentWatchListId) {
        return this.isLoading || !this.hasText
      } else {
         // If in watchlist page, enable button. Action will depend on whether we are editing the query or not
         return false;
      }
    },
    handleClick() {
      if (store.currentWatchListId && !this.isEditingQuery) {
        this.isEditingQuery = true;
        this.$refs.searchInput.value = store.currentWatchList?.queries?.[0]?.text || '';
      }
    },
    getPlaceholderText() {
      if (store.currentWatchListId && !this.isEditingQuery) {
        return store.currentWatchList?.queries?.[0]?.text || '';
      }
      return 'Enter search keywords';
    },
    searchOptionsDisabled() {
      // Search options are disabled if:
      // - We are on a watchlist page and not editing the query
      if (store.currentWatchListId) {
        return !this.isEditingQuery;
      }

      // If we are not on a watchlist page and editing the query, search options are enabled
      return false;
    },

    clearInput() {
      this.isEditingQuery = false;
      this.showSearchResults = false;
      this.removeNonMatchingItems = false;
      this.removeNonMatchingItemsToggled = false;

      this.$refs?.searchInput?.blur();

      // Only clear input if we are on a watchlist page
      if (store.currentWatchListId) {
        if (this.$refs?.searchInput?.value) {
          this.$refs.searchInput.value = '';
        }
        this.searchResults = [];
      }
    },

    handleKeyPress(event) {
      if (event.key === '`') {
        event.preventDefault();
        this.$refs.searchInput.focus();
      } else if (event.key === 'Escape') {
        this.clearInput();
      }
    },

    handleInput(event) {
      this.debouncedHandleInput(event);
    },

    async selectResult(result) {
      const quotedTitle = `"${result.title}"`;
      this.$refs.searchInput.value = quotedTitle;
      this.hasText = true;
      this.searchResults = [];
      // Trigger search immediately
      //this.handleInput({ target: this.$refs.searchInput });
      await this.handleSaveSearch();
    },

    updateHasText(event) {
      this.hasText = event.target.value.trim().length > 0;
    },

    compareSearchResultsWithWatchlist() {
      // Only run comparison if we're on a watchlist page
      if (!store.currentWatchListId) {
        this.comparisonSummary = null;
        return;
      }

      const watchlistEvents = store.currentWatchList.events || {};
      
      // Collect all market IDs from the current watchlist
      const watchlistMarketIds = new Set();
      Object.entries(watchlistEvents).forEach(([eventId, eventData]) => {
        eventData.markets.forEach(marketId => {
          watchlistMarketIds.add(marketId);
        });
      });
      
      // Collect all market IDs from the search results
      const searchResultMarketIds = new Set();
      this.searchResults.forEach(result => {
        result.markets.forEach(market => {
          searchResultMarketIds.add(market.id);
        });
      });
      
      // Count new markets (in search results but not in watchlist)
      let newCount = 0;
      searchResultMarketIds.forEach(marketId => {
        if (!watchlistMarketIds.has(marketId)) {
          newCount++;
        }
      });
      
      // Count removed markets (in watchlist but not in search results)
      let removedCount = 0;
      watchlistMarketIds.forEach(marketId => {
        if (!searchResultMarketIds.has(marketId)) {
          removedCount++;
        }
      });

      
      this.comparisonSummary = { newCount, removedCount };
    },

    handleLiveAddToggle(enabled) {
      console.log('Live add toggle:', enabled);
      if (this.store?.currentWatchList) {
        this.store.currentWatchList.automaticallyAddMatchingEvents = enabled;
      }
    },

    handleMatchTermsToggle(matchAny) {
      console.log('Match terms toggle:', matchAny);
      this.matchAnyTerm = matchAny;

      // Re-trigger search if:

      // 1. We are on the serach page and have text in the input
      if (!store.currentWatchListId && this.$refs.searchInput.value.trim().length > 0) {
        console.log('Re-triggering search');
        this.handleInput({ target: this.$refs.searchInput });
      }

      // 2. We are on a watchlist page and have a query text
      //if (store.currentWatchListId && !this.isEditingQuery) {
      const queryText = store.currentWatchList?.queries?.[0]?.text || '';
      if (store.currentWatchListId && queryText.trim().length > 0) {
        console.log('Re-triggering search');
        this.$refs.searchInput.value = queryText.trim();
        this.$refs.searchInput.focus();
        this.isEditingQuery = true;
        this.handleInput({ target: this.$refs.searchInput });
      }
    },

    openSettings() {
      // This method is no longer needed as we're using the SearchOptions component
    },


    async handleSaveSearch() {
      // Wait for search results to load
      if (this.isLoading) {
        return;
      }

      this.showSearchResults = false;
      this.isLoading = true;

      // If we are on a watchlist page and not editing the query, set the query text and focus on the input
      if (store.currentWatchListId && !this.isEditingQuery) {
        this.isEditingQuery = true;
        this.$refs.searchInput.value = store.currentWatchList?.queries?.[0]?.text || '';
        this.$refs.searchInput.focus();
        return;
      }

      // If we are on a watchlist page and editing the query, update the watchlist
      if (store.currentWatchListId && this.isEditingQuery) {
        const queryText = this.$refs.searchInput.value.trim();

        const events = { ...store.currentWatchList.events };
        
        // Create sets for existing and search result market IDs
        const watchlistMarketIds = new Set();
        const watchlistMarketToEventMap = new Map();
        
        // Map existing markets to their events and build the set of existing market IDs
        Object.entries(events).forEach(([eventId, eventData]) => {
          eventData.markets.forEach(marketId => {
            watchlistMarketIds.add(marketId);
            watchlistMarketToEventMap.set(marketId, eventId);
          });
        });
        
        // Process search results
        const searchResultMarketIds = new Set();
        const marketIdToResultMap = new Map();
        
        this.searchResults.forEach(result => {
          result.markets.forEach(market => {
            searchResultMarketIds.add(market.id);
            marketIdToResultMap.set(market.id, result);
          });
        });
        
        if (!this.removeNonMatchingItems) {
          // CASE 1: Only append new markets
          this.searchResults.forEach(result => {
            result.markets.forEach(market => {
              // Skip if this market is already in the watchlist
              if (watchlistMarketIds.has(market.id)) return;
              
              // Check if the event already exists in the watchlist
              const eventId = result.id;
              
              if (events[eventId]) {
                // Add market to existing event
                events[eventId].markets.push(market.id);
              } else {
                // Create new event entry
                events[eventId] = {
                  markets: [market.id],
                  // Set autoFetch to true only for events (not markets)
                  autoFetch: result.type === 'event'
                };
              }
            });
          });
        } else {
          // CASE 2: Remove non-matching markets and clean up events
          const marketsToRemove = [];
          
          // Find markets that exist in watchlist but not in search results
          watchlistMarketIds.forEach(marketId => {
            if (!searchResultMarketIds.has(marketId)) {
              marketsToRemove.push(marketId);
            }
          });
          
          // Process removals
          marketsToRemove.forEach(marketId => {
            const eventId = watchlistMarketToEventMap.get(marketId);
            if (events[eventId]) {
              // Remove the market from the event
              events[eventId].markets = events[eventId].markets.filter(id => id !== marketId);
              
              // If this was the last market in the event, remove the entire event
              if (events[eventId].markets.length === 0) {
                delete events[eventId];
              } 
              // If autoFetch was true and we're removing markets, set it to false
              else if (events[eventId].autoFetch) {
                events[eventId].autoFetch = false;
              }
            }
          });
          
          // Also add any new markets from search results
          this.searchResults.forEach(result => {
            result.markets.forEach(market => {
              // Skip if this market is already in the watchlist
              if (watchlistMarketIds.has(market.id)) return;
              
              // Check if the event already exists in the watchlist
              const eventId = result.id;
              
              if (events[eventId]) {
                // Add market to existing event
                events[eventId].markets.push(market.id);
              } else {
                // Create new event entry
                events[eventId] = {
                  markets: [market.id],
                  // Set autoFetch to true only for events (not markets)
                  autoFetch: result.type === 'event'
                };
              }
            });
          });
        }
        const query = {
          text: queryText,
          matchAnyTerm: this.matchAnyTerm,
          createdAt: (new Date()).toISOString()
        };
        // Update the watchlist with the modified events and new query
        const success = await updateWatchListQueryAndEvents(events, query);
        
        this.isLoading = false;
        this.isEditingQuery = false;
        this.removeNonMatchingItems = false;
        this.searchResults = [];
        this.clearInput();

        if (success) {
          this.$emit('refetch-events-for-watchlist', store.currentWatchListId);
        }
        return;
      }

      // If we in the search page i.e. not on a watchlist page, save the search results to a new watchlist
      if (!store.currentWatchListId) {
        // If not, create a new watchlist and redirect to it
        const queryText = this.$refs.searchInput.value.trim();

        this.handleBlur();
        
        // Perform one final search 
        try {
          const response = await apiClient.get(`https://api.nevua.markets/search?query=${encodeURIComponent(queryText)}&matchAny=${this.matchAnyTerm}`);
          if (response.status !== 200 || !response.data ) {
            this.isLoading = false;
            return; 
          }
          this.searchResults = [...response.data]
        } catch (error) {
          Sentry.setExtra('method', 'handleSaveSearch');
          Sentry.captureException(error);
          console.error('Final search validation error:', error);
          this.isLoading = false;
          return;
        }

        const query = {
          text: queryText,
          matchAnyTerm: this.matchAnyTerm,
          createdAt: (new Date()).toISOString()
        };

        // Save search results manages authenticated and unauthenticated users
        const newWatchListId = await saveSearchResultsToNewWatchList([...this.searchResults], query);
        
        this.searchResults = [];
        this.isLoading = false;

        this.$router.push(`/l/${newWatchListId}`);
        return;
      }
    },

    startLogoFlipping() {
      this.flipInterval = setInterval(() => {
        this.flipLogo();
      }, 5000);
    },
    
    flipLogo() {
      // Get the next logo index
      this.nextLogoIndex = (this.currentLogoIndex + 1) % this.logos.length;
      
      // Flip to the back side (180 degrees)
      this.flipDegrees = 180;
      
      // After animation completes, update the front side content
      setTimeout(() => {
        // Update the current logo to be shown on the front face next time
        this.currentLogoIndex = this.nextLogoIndex;
        
        // Disable the transition temporarily
        if (this.$refs.logoContainer) {
          this.$refs.logoContainer.style.transition = 'none';
          
          // Reset to 0 degrees without animation
          this.flipDegrees = 0;
          
          // Force browser to apply the style change before re-enabling transition
          setTimeout(() => {
            if (this.$refs.logoContainer) {
              this.$refs.logoContainer.style.transition = 'transform 1s';
            }
          }, 50);
        }
      }, 1000); // Match the transition duration
    },
  },
};
</script>

<style scoped>
/* .caret-transparent {
  caret-color: initial;
} */

.logo-flipper {
  perspective: 1000px;
  display: inline-flex;
  /* min-width: 100px;
  height: 100%; */
  /* height: 24px; */
}

.logo-container {
  transition: transform 1s;
  transform-style: preserve-3d;
  /* position: relative; */
  width: 100%;
  height: 100%;
}

.logo-face {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  /* align-items: center; */
  /* justify-content: center; */
  gap: 4px;
}

.logo-front {
  transform: rotateX(0deg);
  z-index: 2;
}

.logo-back {
  transform: rotateX(180deg);
  z-index: 1;
}
</style>